/**
 * @packageDocumentation
 * Implements the animated loading screen shown before gameplay, including progress display and the transition into the main app.
 */
import * as PIXI from "pixi.js";   
import { Assets } from "pixi.js"; 
import { loadGameAssets, loadPreloadAssets } from "./LoadAssets";
import { InstructionScreen } from "./instruction";
import { SoundPrompt } from "./Game/soundPrompt";
import { Button } from "./lib/buttonCreator";
import { SoundManager } from "./Game/sounds";
import { Spine } from "@esotericsoftware/spine-pixi-v8"; 
import { font_main } from "./Game/global";

/**
 * Preloader class that handles the loading screen and assets preloading
 * @extends PIXI.Container
 */
export class Preloader extends PIXI.Container {
  /** The game logo sprite */
  private logo!:PIXI.Sprite
  /** The loading animation spine object */
  private preload_anim!:Spine
  /** The loading progress text */
  private loadingText!:PIXI.Text
  /** The play button that appears after loading */
  // private Playbut!:Button
  private soundPrompt?: SoundPrompt
  private instructionScreen?: InstructionScreen
  private soundConsent: boolean | null = null

  /**
   * Creates an instance of Preloader
   * @param app_width - Width of the application
   * @param app_height - Height of the application 
   * @param playClickFun - Callback function when play button is clicked
   */
  constructor(private app_width:number,private app_height:number,private playClickFun:()=>void ){ 
      super(); 
      this.sortableChildren = true;
      (async()=>{
       await loadPreloadAssets(); 
       this.createLogo()
       this.createPreloadAnim()
       this.createLoadingText()
      //  this.createPlaybutton()
       if(window.matchMedia("(orientation:portrait)").matches){
        this.PortraitAdjust_preloader();
      }else{
        this.landscapeAdjust_preloader();
      }  
      this.progressLoading() 

      })() 
     }

     /**
      * Creates and positions the game logo
      */
     createLogo(){
      this.logo = new PIXI.Sprite(Assets.get("logo")) 
      this.logo.x = this.app_width/2;
      this.logo.y = this.app_height/2-180//+200
      this.logo.anchor.set(0.5)
      this.logo.scale.set(0.4)
      this.addChild(this.logo);
     }

     /**
      * Creates and positions the loading animation
      */
     createPreloadAnim(){
      this.preload_anim = Spine.from({ skeleton: "preloader_anim_json", atlas: "preloader_anim_atlas",scale:1 }); 
      this.preload_anim.x = this.app_width/2;
      this.preload_anim.y = this.app_height/2+170;
      this.preload_anim.state.timeScale = 0.4; 
      this.preload_anim.state.setAnimation(0,"animation",true) 
      this.preload_anim.pivot.set(0);
      this.addChild(this.preload_anim); 
     }

     /**
      * Creates and positions the loading progress text
      */
     createLoadingText(){
       this.loadingText = new PIXI.Text({text:'0%',style:new PIXI.TextStyle({
        fill: '#fff',
        stroke: {
          color:'#000',
          width:5
        },
        fontSize: 40,
        fontFamily:font_main
        })});
        this.loadingText.anchor.set(0.5);
         this.loadingText.x = this.app_width/2-5+5;
         this.loadingText.y = this.app_height / 2-35+206; 
         this.addChild(this.loadingText); 
     }

     /**
      * Creates and positions the play button
      */
    //  createPlaybutton(){
    //   this.Playbut = new Button("start_btn",{text:"PLAY",fontFamily:font_main,fontSize:40}); 
    //   this.Playbut.addInteraction(()=>{
    //    SoundManager.Btnclick_1SndPlay(); 
    //    this.playClickFun()
    //   })
    //   this.Playbut.setTextPosition(0,-3)
    //   this.Playbut.x = this.app_width/2;
    //   this.Playbut.y = this.app_height/2+200;
    //  }

    /**
     * Handles the loading progress and updates the UI
     */
    async progressLoading(){
      await loadGameAssets((progress: number) => {
        const percentage = Math.floor(progress * 100);
        this.loadingText.text = `${percentage}%`;
      }); 
      setTimeout(() => {
        this.removeChild(this.loadingText); 
        this.removeChild(this.preload_anim);  
        // this.addChild(this.Playbut);
        this.removeChild(this.logo);

        this.promptSoundPreference()
      }, 1000); 

     } 

    private promptSoundPreference(){
      if(this.soundPrompt){
        this.removeChild(this.soundPrompt);
        this.soundPrompt.destroy({ children: true });
        this.soundPrompt = undefined;
      }
      const prompt = new SoundPrompt({
        width: this.app_width,
        height: this.app_height,
        onChoice: (enabled:boolean) => this.onSoundPreferenceSelected(enabled)
      });
      this.soundPrompt = prompt;
      this.addChild(prompt);
    }

    private onSoundPreferenceSelected(enabled:boolean){
      this.soundConsent = enabled;
      SoundManager.setBgSound(enabled);
      SoundManager.setGameSound(enabled);
      
      if(this.soundPrompt) {
        this.removeChild(this.soundPrompt);
            // overlay.style.display = "none"; 
        
        this.soundPrompt.destroy({ children: true });
        this.soundPrompt = undefined;
      }
      this.showInstructionScreen();
    }

    private showInstructionScreen(){
      if(this.instructionScreen){
        return;
      }


      if (this.getSoundConsent()) { // BG Music control is here
        SoundManager.bgMusicSndPlay();
      } else {
        SoundManager.setBgSound(false);
        SoundManager.setGameSound(false);
        SoundManager.MuteBgMusicFun();
        SoundManager.MuteGameSoundFun();
      }


      const instructionScreen = new InstructionScreen(
        this.app_width,
        this.app_height,
        this.playClickFun
      );
      instructionScreen.zIndex = 10;
      this.instructionScreen = instructionScreen;
      this.addChild(instructionScreen);
    }

    getSoundConsent(){
      return this.soundConsent === true;
    }

    /**
     * Adjusts UI elements for landscape orientation
     */
    landscapeAdjust_preloader (){
      this.logo.scale.set(0.5)
      this.preload_anim.scale.set(0.35)
      this.loadingText.scale.set(1)
      // this.Playbut.scale.set(1)
      this.loadingText.y = this.app_height / 2-35+201; 
    }

    /**
     * Adjusts UI elements for portrait orientation
     */
    PortraitAdjust_preloader (){
      this.logo.scale.set(0.25)
      this.preload_anim.scale.set(0.25)
      this.loadingText.scale.set(0.6)
      // this.Playbut.scale.set(0.6)
      this.loadingText.y = this.app_height / 2-35+185+16+2; 
  }

 }
  
//....................................................................  

//.................................................................... 

//....................................................................  
//.................................................................... 

